import './index.scss';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Global } from '@emotion/react';
import { Flex } from '@chakra-ui/react';
import { constants } from '@sj/sj-theme';
import {
  createRoutesFromChildren,
  HashRouter,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { AppProvider } from '@frx/frx-react-context';
import * as Sentry from '@sentry/react';
import { ExtraErrorData } from '@sentry/integrations';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import reportWebVitals from './reportWebVitals';
import { Loader, ScrollToTop } from './components';
import { getSentryConfig } from './environment';
import { ChakraProvider } from './ChakraProvider';

const sentryConfig = getSentryConfig(window.location);

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_PROJECT_DSN,
  enabled: sentryConfig.enabled,
  release: sentryConfig.release,
  environment: sentryConfig.environment,
  attachStacktrace: true,
  // This error is from Instagram browser and made sure no user impacted so far
  ignoreErrors: [
    /Can't find variable: _AutofillCallbackHandler/i,
    /Can't find variable: jQuery/i,
    /jQuery is not defined/i,
  ],
  // These domains are external for our app
  denyUrls: [
    /https:\/\/assets\.adobedtm\.com/,
    /https:\/\/cdn\.cookielaw\.org/,
    /https:\/\/maps\.googleapis\.com/,
    /https:\/\/s.go-mpulse\.net/,
  ],
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
    new ExtraErrorData(),
    new Sentry.Replay({
      maskAllText: false,
      maskAllInputs: true,
      blockAllMedia: false,
      networkDetailAllowUrls: [
        'https://fundraising.qa.stjude.org/',
        'https://fundraising.stjude.org/',
      ],
    }),
  ],
  sampleRate: sentryConfig.sampleRate,
  tracesSampleRate: sentryConfig.tracesSampleRate,
  replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <ChakraProvider>
        <Global styles={constants.SJ_FONT_FACE} />
        <QueryClientProvider client={queryClient}>
          <AppProvider
            loader={
              <Flex alignItems="center" justifyContent="center" h="50vh">
                <Loader />
              </Flex>
            }
          >
            <HashRouter>
              <ScrollToTop />
              <App />
            </HashRouter>
          </AppProvider>
        </QueryClientProvider>
      </ChakraProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

/** These are TR Global styles, once in QA/PROD our own styles gets overridden because of this file getting added
 * So removing it for this project. All the global styles should be added as part of Chakra theme */
document.querySelector('link[href*="../css/UserGlobalStyle.css"]')?.remove();
document.querySelector('link[href*="../css/CustomStyle.css"]')?.remove();
